import React, { useMemo, useState } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import type { AvailableLessonForCredit } from '../../services/makeupCredit';
import { formatTime, timeToMinutes } from '../../utils/timeFormat';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface CompatibleLessonsWeeklyViewProps {
  lessons: AvailableLessonForCredit[];
  termStart?: Date;
  termEnd?: Date;
  onSelectSlot: (slot: AvailableLessonForCredit) => void;
  creditExpiresAt?: string;
  /** Key of slot currently being processed (e.g. "lessonId-date") */
  isSlotLoading?: string | null;
}

export function CompatibleLessonsWeeklyView({
  lessons,
  termStart,
  termEnd,
  onSelectSlot,
  isSlotLoading,
}: CompatibleLessonsWeeklyViewProps) {
  const [displayWeekStart, setDisplayWeekStart] = useState(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    if (termStart && termEnd) {
      if (now < termStart) return startOfWeek(termStart, { weekStartsOn: 1 });
      if (now > termEnd) return startOfWeek(termEnd, { weekStartsOn: 1 });
    }
    return weekStart;
  });

  const displayWeekEnd = addDays(displayWeekStart, 6);
  const canPrev = !termStart || displayWeekStart > termStart;
  const canNext = !termEnd || displayWeekEnd < termEnd;

  const slotsInWeek = useMemo(() => {
    return lessons.filter((l) => {
      const d = new Date(l.date);
      return d >= displayWeekStart && d <= displayWeekEnd;
    });
  }, [lessons, displayWeekStart, displayWeekEnd]);

  const slotsByDate = useMemo(() => {
    const map = new Map<string, AvailableLessonForCredit[]>();
    slotsInWeek.forEach((s) => {
      const key = format(new Date(s.date), 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    return map;
  }, [slotsInWeek]);

  const getStartTime = (time: string) => (time.includes('-') ? time.split('-')[0] : time);

  const { earliestTime, latestTime } = useMemo(() => {
    if (slotsInWeek.length === 0) return { earliestTime: 8 * 60, latestTime: 20 * 60 };
    let earliest = 24 * 60;
    let latest = 0;
    slotsInWeek.forEach((s) => {
      const mins = timeToMinutes(getStartTime(s.time));
      if (mins < earliest) earliest = mins;
      if (mins + 60 > latest) latest = mins + 60;
    });
    earliest = Math.max(0, Math.floor(earliest / 30) * 30 - 60);
    latest = Math.min(24 * 60, Math.ceil(latest / 30) * 30 + 60);
    return { earliestTime: earliest, latestTime: latest };
  }, [slotsInWeek]);

  const totalRange = latestTime - earliestTime;

  return (
    <div className="w-full overflow-x-auto">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setDisplayWeekStart((w) => subWeeks(w, 1))}
            disabled={!canPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-lg font-semibold text-gray-900 dark:text-white min-w-[180px] text-center">
            {format(displayWeekStart, 'MMM d')} – {format(displayWeekEnd, 'MMM d, yyyy')}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setDisplayWeekStart((w) => addWeeks(w, 1))}
            disabled={!canNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="grid grid-cols-8" style={{ minHeight: '120px' }}>
          <div className="bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-2">
            <div className="font-semibold text-gray-900 dark:text-white text-sm">Time</div>
          </div>
          {[0, 1, 2, 3, 4, 5, 6].map((colIndex) => {
            const dayDate = addDays(displayWeekStart, colIndex);
            return (
              <div
                key={colIndex}
                className="bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 last:border-r-0 p-2 text-center relative z-0"
              >
                <div className="font-semibold text-gray-900 dark:text-white text-sm">
                  {DAYS_SHORT[dayDate.getDay()]}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {format(dayDate, 'MMM d')}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-8 relative" style={{ minHeight: `${(totalRange / 30) * 36}px` }}>
          <div className="border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky left-0 z-0 relative">
            {Array.from({ length: Math.ceil(totalRange / 60) }, (_, i) => earliestTime + i * 60).map(
              (slotM) => {
                const pos = ((slotM - earliestTime) / totalRange) * 100;
                const h = Math.floor(slotM / 60);
                const m = slotM % 60;
                const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                return (
                  <div
                    key={slotM}
                    className="absolute left-0 right-0 px-1 text-xs text-gray-600 dark:text-gray-400"
                    style={{ top: `${pos}%` }}
                  >
                    {formatTime(timeStr)}
                  </div>
                );
              }
            )}
          </div>

          {[0, 1, 2, 3, 4, 5, 6].map((colIndex) => {
            const dayDate = addDays(displayWeekStart, colIndex);
            const dateKey = format(dayDate, 'yyyy-MM-dd');
            const daySlots = slotsByDate.get(dateKey) ?? [];

            return (
              <div
                key={colIndex}
                className="border-r border-gray-200 dark:border-gray-700 last:border-r-0 relative bg-white dark:bg-gray-900 z-10"
              >
                {daySlots.map((slot) => {
                  const startM = timeToMinutes(getStartTime(slot.time));
                  const topPercent = ((startM - earliestTime) / totalRange) * 100;
                  const heightPercent = Math.max(8, (60 / totalRange) * 100);
                  const slotKey = `${slot.lessonId}-${slot.date}`;
                  const isLoading = isSlotLoading === slotKey;
                  const spots = slot.spotsAvailable ?? 0;
                  const isUnlimitedSpots = spots > 9990;
                  const isFull = !isUnlimitedSpots && spots === 0;
                  const isLowAvailability =
                    !isUnlimitedSpots && spots > 0 && spots <= 3;

                  const baseBtn =
                    'absolute left-1 right-1 rounded p-1.5 text-left text-xs font-medium border z-20';
                  const fullClasses =
                    'border-gray-300 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed';
                  const openClasses =
                    'border-purple-300 bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100 hover:shadow-md cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed';

                  return (
                    <button
                      key={`${slot.lessonId}-${slot.date}-${slot.time}`}
                      type="button"
                      onClick={() => {
                        if (isFull) return;
                        onSelectSlot(slot);
                      }}
                      disabled={!!isSlotLoading || isFull}
                      className={`${baseBtn} ${isFull ? fullClasses : openClasses}`}
                      style={{
                        top: `${topPercent}%`,
                        height: `${heightPercent}%`,
                        minHeight: '32px',
                      }}
                    >
                      <div className="truncate font-medium">{slot.lessonName}</div>
                      <div className="truncate opacity-90 text-[10px]">{formatTime(getStartTime(slot.time))}</div>
                      {isLowAvailability && (
                        <div className="truncate text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">
                          {spots} spot{spots === 1 ? '' : 's'} left
                        </div>
                      )}
                      <span
                        className={`inline-block mt-0.5 px-1 py-0 rounded text-[10px] ${
                          isFull
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                            : 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200'
                        }`}
                      >
                        {isLoading ? 'Booking…' : isFull ? 'Full' : 'Use Credit'}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
